import React, { useEffect, useState } from "react";
import marketingAPI from "../api/marketingAPI";
import AdminNavbar from "./AdminNavbar";

const AdminAssignedLocations = () => {
  const [locations, setLocations] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingLocation, setEditingLocation] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await marketingAPI.get("/all", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setLocations(res.data.data);
        setFiltered(res.data.data);
      }
    } catch (err) {
      alert("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filters
  useEffect(() => {
    let data = [...locations];

    if (searchTerm) {
      data = data.filter((loc) =>
        (loc.assignedTo?.name || loc.assignedToName)
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (dateFilter) {
      data = data.filter((loc) => {
        const d = new Date(loc.createdAt).toISOString().split("T")[0];
        return d === dateFilter;
      });
    }

    setFiltered(data);
  }, [searchTerm, dateFilter, locations]);

  // ✅ DELETE
  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");
    if (!window.confirm("Delete this location?")) return;

    await marketingAPI.delete(`/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    fetchLocations();
  };

  // ✅ SAVE EDIT
  const saveEdit = async () => {
    const token = localStorage.getItem("token");

    await marketingAPI.put(
      `/${editingLocation._id}`,
      {
        address: editingLocation.address,
        description: editingLocation.description,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    setEditingLocation(null);
    fetchLocations();
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-8 pt-24">
      <AdminNavbar />

      <h2 className="text-4xl font-bold font-mono text-purple-700 mb-6 text-center mt-4">
        Assigned Marketing Locations
      </h2>

      <div className="max-w-9xl mx-auto bg-white p-8 rounded-2xl shadow-xl">
        {/* 🔍 Search + Date */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-6">
          <h3 className="text-2xl font-semibold text-purple-600">
            Location List
          </h3>

          <div className="flex gap-4 w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by Assignee Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border px-5 py-3 text-lg rounded-xl w-full md:w-80 shadow"
            />

            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border px-4 py-3 text-lg rounded-xl shadow"
            />
          </div>
        </div>

        {/* 📋 Table */}
        <div className="overflow-y-auto max-h-[600px] border rounded-xl">
          <table className="min-w-full table-auto text-lg">
            <thead className="bg-purple-100 text-purple-800 sticky top-0 z-10">
              <tr>
                <th className="p-5">Assignee</th>
                <th className="p-5">Email</th>
                <th className="p-5">Address</th>
                <th className="p-5">Description</th>
                <th className="p-5">Status</th>
                <th className="p-5">Assigned Date</th>
                <th className="p-5">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((loc) => (
                <tr
                  key={loc._id}
                  className="text-center border-t hover:bg-purple-50"
                >
                  <td className="p-5 font-bold text-purple-700">
                    {loc.assignedTo?.name || loc.assignedToName}
                  </td>

                  <td className="p-5">
                    {loc.assignedTo?.email || loc.assignedToEmail}
                  </td>

                  <td className="p-5 text-left max-w-md">{loc.address}</td>

                  <td className="p-5 text-left max-w-sm">{loc.description}</td>

                  <td className="p-5">
                    <span
                      className={`px-4 py-2 rounded-full text-white text-sm font-bold ${
                        loc.status === "pending"
                          ? "bg-yellow-500"
                          : loc.status === "completed"
                          ? "bg-green-600"
                          : "bg-gray-500"
                      }`}
                    >
                      {loc.status}
                    </span>
                  </td>

                  <td className="p-5">
                    {new Date(loc.createdAt).toLocaleString()}
                  </td>

                  {/* ✅ ACTIONS */}
                  <td className="p-5 flex gap-3 justify-center">
                    <button
                      onClick={() => setEditingLocation(loc)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(loc._id)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="text-center p-6 text-gray-500 text-lg">
              No locations found
            </div>
          )}
        </div>
      </div>

      {/* ✅ EDIT MODAL */}
      {editingLocation && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-xl w-[500px]">
            <h2 className="text-2xl font-bold mb-4">Edit Location</h2>

            <textarea
              value={editingLocation.address}
              onChange={(e) =>
                setEditingLocation({
                  ...editingLocation,
                  address: e.target.value,
                })
              }
              className="w-full border p-3 rounded mb-3"
            />

            <textarea
              value={editingLocation.description}
              onChange={(e) =>
                setEditingLocation({
                  ...editingLocation,
                  description: e.target.value,
                })
              }
              className="w-full border p-3 rounded mb-3"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditingLocation(null)}
                className="px-4 py-2 bg-gray-400 text-white rounded"
              >
                Cancel
              </button>

              <button
                onClick={saveEdit}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAssignedLocations;
